/**
 * Client-side integrations bootstrap.
 *
 * Same pattern as the customer-web counterpart. Fetches
 * /platform-config/integrations once on app boot, hydrates
 * window.__TENANT_CONFIG__ with the provider blocks (firebase, stripe, etc),
 * then notifies subscribers. Render isn't blocked; auth init waits on this.
 *
 * Solves R2-staleness: dashboard saves to Postgres (integrations_json), but
 * R2's tenant config.json may not be updated, so middleware-injected
 * __TENANT_CONFIG__ misses firebase. This bootstrap pulls live values
 * straight from the backend on the client.
 */

type Listener = () => void

let promise: Promise<void> | null = null
const listeners = new Set<Listener>()

export function loadIntegrations(apiBaseUrl: string): Promise<void> {
  if (promise) return promise
  promise = (async () => {
    try {
      const url = `${apiBaseUrl.replace(/\/+$/, '')}/platform-config/integrations`
      // Bounded retry: this fetch runs exactly once per session, so a
      // single transient failure (worker cold start, network blip) used
      // to leave the whole session without maps/firebase keys — the
      // intermittent "map not loaded until reload" reports. Three
      // attempts with short backoff absorb blips.
      let res: Response | null = null
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise((r) => setTimeout(r, attempt * 1500))
        try {
          res = await fetch(url, { credentials: 'omit' })
          if (res.ok) break
          console.warn(`[integrations-bootstrap] fetch failed (attempt ${attempt + 1}):`, res.status)
        } catch (err) {
          res = null
          console.warn(`[integrations-bootstrap] fetch error (attempt ${attempt + 1}):`, err)
        }
      }
      if (!res || !res.ok) return
      const data = (await res.json()) as {
        integrations?: Record<string, { enabled?: boolean; credentials?: Record<string, unknown> }>
        cardPaymentsEnabled?: boolean
        codEnabled?: boolean
      }
      const integ = data.integrations ?? {}
      const tc = (window.__TENANT_CONFIG__ ?? {}) as Record<string, unknown>
      for (const [provider, entry] of Object.entries(integ)) {
        tc[provider] = {
          enabled: entry?.enabled === true,
          ...(entry?.credentials ?? {}),
        }
      }
      if (data.cardPaymentsEnabled !== undefined) tc.cardPaymentsEnabled = data.cardPaymentsEnabled
      if (data.codEnabled !== undefined) tc.codEnabled = data.codEnabled
      ;(window as unknown as { __TENANT_CONFIG__: typeof tc }).__TENANT_CONFIG__ = tc
    } catch (err) {
      console.warn('[integrations-bootstrap] error:', err)
    } finally {
      for (const l of listeners) {
        try { l() } catch (e) { console.error('[integrations-bootstrap] listener threw:', e) }
      }
      listeners.clear()
    }
  })()
  return promise
}

export function onIntegrationsReady(listener: Listener): void {
  if (promise && (promise as Promise<void> & { _done?: boolean })._done) {
    listener()
    return
  }
  listeners.add(listener)
  void (promise ?? Promise.resolve()).then(() => {
    ;(promise as unknown as { _done?: boolean })._done = true
  })
}
